import React, { useState, useEffect } from 'react';
import { useUser } from '../../api/users';
import { FaCalendar, FaClock, FaUsers, FaMapMarkerAlt, FaEdit, FaTimes } from 'react-icons/fa';
import { supabaseFreeTable } from '../../utils/supabase/clientFreeTable';
import { supabase } from '../../utils/supabase/client';
import UserDetailsModal from '../../Screens/UserDetails/UserDetails';
import { useUpdateReservationFreeTable, useUpdateReservationTime } from '../../api/reservations';
import { useAuth } from '../../api/AuthProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useRestaurant } from '../../api/restaurant';

const ReservationCard = ({
  reservation,
  tempPartySize,
  handleConfirm,
  handleDeny,
  setSelectedReservationId,
}) => {
  const { data: user, isLoading, error } = useUser(reservation.userid);
  const { profile } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { mutate: UpdateReservation } = useUpdateReservationFreeTable();
  const { mutate: SuggestTime, isPending } = useUpdateReservationTime();
  const queryClient = useQueryClient();
  const { data: restaurant } = useRestaurant();
  const [hasChangedTime,setHasChangedTime] = useState(false); 
  const [hasChangedLocation,setHasChangedLocation] = useState(false); 


  const [time, setTime] = useState(reservation.time.slice(0, 5));
  const [newSeatingLocation, setNewSeatingLocation] = useState(reservation.location);

const handleSubmit = async (status, suggestedTime = reservation.time, suggestedLocation = reservation.location) => {
  console.log(hasChangedTime)
  console.log(hasChangedLocation)
  try {
    if (!user?.phonenumber && !user?.email) {
      console.error('User must have a phone number or email address.');
      return;
    }

    let guestId;
    const { data: existingCustomer, error: customerError } = await supabase
      .from('customers')
      .select('id, reservations')
      .eq('phone', user.phonenumber)
      .eq('restaurant_id', profile.restaurantid)
      .eq('email', user.email)
      .eq('name', user.full_name)
      .single();

    if (customerError && customerError.code !== 'PGRST116') {
      throw new Error('Error checking customer: ' + customerError.message);
    }

    if (existingCustomer) {
      guestId = existingCustomer.id;
      const { error: updateError } = await supabase
        .from('customers')
        .update({ reservations: existingCustomer.reservations + 1 })
        .eq('id', guestId);

      if (updateError) {
        throw new Error('Error updating customer reservations: ' + updateError.message);
      }
    } else {
      const { data: newCustomer, error: createError } = await supabase
        .from('customers')
        .insert([
          {
            phone: user.phonenumber,
            email: user.email || null,
            name: user.full_name || null,
            restaurant_id: profile.restaurantid,
            reservations: 1,
          },
        ])
        .select('id')
        .single();

      if (createError) {
        throw new Error('Error creating customer: ' + createError.message);
      }
      guestId = newCustomer.id;
    }

    const reservationDateTime = new Date(`${reservation.date}T${reservation.time.slice(0, 5) !== time ? suggestedTime : reservation.time}`);

    const reservationData = {
      guest_id: guestId,
      name: user.full_name,
      partySize: parseInt(reservation.people),
      time: reservationDateTime.toISOString(),
      restaurantid: profile.restaurantid,
      tableIds: [],
      prefered_place: reservation.location !== newSeatingLocation ? suggestedLocation : reservation.location,
      freeTableId: reservation.id,
      freeTableUser: reservation.userid,
      additionalRequest: reservation.additionalInfo,
      discount: restaurant.discount,
      confirmedBy: profile.full_name,
      status: status == "CONFIRMED" ? "pending" : status,
    };

    const { error: reservationError } = await supabase
      .from('reservations')
      .insert([reservationData]);

    if (reservationError) {
      throw new Error('Error creating reservation: ' + reservationError.message);
    }
  } catch (err) {
    console.error('Error in handleSubmit:', err.message);
  }
  queryClient.invalidateQueries(['reservations', 'freeTable']);
};

  async function sendPushNotification(token, title, body) {
    try {
      const { data, error } = await supabaseFreeTable.functions.invoke('sendPushNotification', {
        body: { token, title, body },
      });
      console.log('Push notification response:', data);
      if (error) {
        console.error('Error sending push notification:', error);
        return;
      }
    } catch (error) {
      console.error('Unexpected error in sendPushNotification:', error);
    }
  }

  const convertDateToShow = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  const timeString = reservation.time;

  const guestDisplay = isLoading
    ? 'Loading...'
    : error
    ? 'Error'
    : user?.full_name || 'Unknown';

  const ConfirmReservation = async () => {
    try {
      UpdateReservation({ id: reservation.id, status: 'CONFIRMED' });
      await sendPushNotification(
        user.expo_push_token,
        `Вашата резервација е потврдена`,
        `Вашата резервација за ${convertDateToShow(reservation.date)} во ${reservation.time} е потврдена`
      );
      await handleSubmit('CONFIRMED');
    } catch (err) {
      console.error('Error confirming reservation:', err.message);
    }
  };

  const DeclineReservation = async () => {
    try {
      UpdateReservation({ id: reservation.id, status: 'DECLINED' });
      await sendPushNotification(
        user.expo_push_token,
        `Вашата резервација е одбиена`,
        `Вашата резервација за ${convertDateToShow(reservation.date)} во ${reservation.time} е одбиена`
      );
      await handleSubmit('DECLINED');
    } catch (err) {
      console.error('Error declining reservation:', err.message);
    }
  };

  const getTime = () => {
    let times = [];
    const start = new Date();
    start.setHours(restaurant.reservationFrom, 0, 0);
    const end = new Date();
    end.setHours(restaurant.reservationTo, 0, 0);
    while (start <= end) {
      times.push(start.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      start.setMinutes(start.getMinutes() + 30);
    }
    return times.map((time, index) => ({
      label: time,
      value: time,
    }));
  };

  const displayVreme = getTime();
  const displayLocacii = restaurant.lokacii.map((lokacija, index) => ({
    label: lokacija.toString(),
    value: lokacija.toString(),
  }));

const handleSuggestTime = async () => {
  try {
    // Check if there are changes to time or location
    const hasChanges = reservation.time.slice(0, 5) !== time || reservation.location !== newSeatingLocation;
    if (!hasChanges) {
      console.log('No changes to time or location');
      setIsEditModalOpen(false);
      return;
    }
    // Set states based on individual changes
    if (reservation.time.slice(0, 5) !== time) {
            console.log("changed time")

      setHasChangedTime(true);
    }
    if (reservation.location !== newSeatingLocation) {
      console.log("changed location")
      setHasChangedLocation(true);
    }

    await SuggestTime(
      {
        id: reservation.id,
        time: reservation.time.slice(0, 5) === time ? null : time,
        location: reservation.location === newSeatingLocation ? null : newSeatingLocation,
      },
      {
        onSuccess: async () => {
          console.log('SuggestTime mutation succeeded');
          setIsEditModalOpen(false);
        },
        onError: (error) => {
          console.error('SuggestTime mutation failed:', error.message);
        },
      }
    );

    // Send push notification
    await sendPushNotification(
      user.expo_push_token,
      'Барање за промена на време',
      `Барање за промена на време на вашата резервација во ${restaurant.name || 'нашето место'}`
    );
    await handleSubmit('PENDING', time, newSeatingLocation); // Insert reservation with WAITINGCONFIRMATION status and new time/location
  } catch (err) {
    console.error('Error suggesting time:', err.message);
  }
};

  return (
    <>
      <div
        style={{
          width: '90%',
          maxWidth: '600px',
          borderRadius: '12px',
          margin: '20px auto',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          padding: '16px',
          transition: 'transform 0.2s ease-in-out',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      >
        {/* Header: User Name and Edit Button */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
          }}
        >
          <button
            style={{
              fontSize: '22px',
              margin: '0 0 0 16px',
              color: '#3eb488',
              fontWeight: '700',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'color 0.2s ease-in-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#2a9066')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#3eb488')}
          >
            {guestDisplay}
          </button>
          
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsEditModalOpen(true);
              }}
            >
              <FaEdit size={28} color="#3eb488" style={{ transition: 'color 0.2s ease-in-out' }} />
            </button>
        
        </div>

        {/* Discount Banner */}
        {restaurant.discount > 0 && (
          <div
            style={{
              textAlign: 'center',
              backgroundColor: '#3eb488',
              padding: '12px',
              color: '#ffffff',
              fontWeight: '600',
              width: '95%',
              margin: '8px auto',
              borderRadius: '10px',
              fontSize: '16px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            Оваа резервација има {restaurant.discount}% попуст
          </div>
        )}

        {/* Info Section */}
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
              <FaCalendar size={28} color="#3eb488" />
              <span style={{ marginLeft: '8px', fontWeight: '500', fontSize: '16px', color: '#333' }}>
                {convertDateToShow(reservation.date)}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
              <FaClock size={28} color="#3eb488" />
              <span style={{ marginLeft: '8px', fontWeight: '500', fontSize: '16px', color: '#333' }}>
                {timeString}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', padding: '0 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
              <FaUsers size={28} color="#3eb488" />
              <span style={{ marginLeft: '8px', fontWeight: '500', fontSize: '16px', color: '#333' }}>
                {reservation.people || 0}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '120px' }}>
              <FaMapMarkerAlt size={28} color="#3eb488" />
              <span style={{ marginLeft: '8px', fontWeight: '500', fontSize: '16px', color: '#333' }}>
                {reservation.location || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        {reservation.additionalInfo && (
          <>
            <div
              style={{
                borderBottom: '2px solid #3eb488',
                paddingBottom: '8px',
                margin: '16px auto 8px auto',
                width: '85%',
              }}
            >
              <span
                style={{
                  color: '#3eb488',
                  textAlign: 'center',
                  fontWeight: '700',
                  fontSize: '22px',
                  display: 'block',
                }}
              >
                Дополнително барање
              </span>
            </div>
            <span
              style={{
                textAlign: 'center',
                fontSize: '18px',
                width: '90%',
                display: 'block',
                margin: '0 auto',
                color: '#333',
              }}
            >
              {reservation.additionalInfo}
            </span>
          </>
        )}

        {/* Confirm and Decline Buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '16px 0',
            gap: '12px',
          }}
        >
          <button
            style={{
              backgroundColor: '#e74c3c',
              padding: '12px',
              borderRadius: '8px',
              width: '140px',
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
            DeclineReservation()
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#c0392b')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#e74c3c')}
          >
            Одбиј
          </button>
          <button
            style={{
              backgroundColor: '#3eb488',
              padding: '12px',
              borderRadius: '8px',
              width: '140px',
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: '600',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              ConfirmReservation();
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2a9066')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#3eb488')}
          >
            Потврди
          </button>
        </div>

        {/* User Details Modal */}
        <UserDetailsModal
          userid={reservation.userid}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </div>

      {/* Edit Time and Location Modal */}
      {isEditModalOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '450px',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              transform: 'translateY(0)',
              transition: 'transform 0.3s ease-in-out',
            }}
          >
            {isPending ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '28px', fontWeight: '700', marginBottom: '16px', color: '#333' }}>
                  Резервацијата се ажурира
                </p>
                <p style={{ fontSize: '22px', fontWeight: '400', marginBottom: '40px', color: '#555' }}>
                  Ве молиме почекајте
                </p>
                <div
                  style={{
                    border: '5px solid #f0f0f0',
                    borderTop: '5px solid #3eb488',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto',
                  }}
                ></div>
              </div>
            ) : (
              <>
                <button
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    color: '#3eb488',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease-in-out',
                  }}
                  onClick={() => setIsEditModalOpen(false)}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#2a9066')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#3eb488')}
                >
                  <FaTimes size={32} />
                </button>
                <div style={{ marginTop: '48px' }}>
                  <p
                    style={{
                      textAlign: 'center',
                      color: '#3eb488',
                      fontSize: '32px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {guestDisplay}
                  </p>
                </div>
                <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '90%',
                      padding: '16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '12px',
                      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.1)',
                      marginBottom: '24px',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <FaClock size={34} color="#3eb488" style={{ marginRight: '16px' }} />
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '20px',
                          fontWeight: '500',
                          marginBottom: '8px',
                          color: '#333',
                        }}
                      >
                        Ново време
                      </label>
                      <select
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #3eb488',
                          borderRadius: '8px',
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#333',
                          backgroundColor: '#ffffff',
                          outline: 'none',
                          transition: 'border-color 0.2s ease-in-out',
                        }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#2a9066')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = '#3eb488')}
                      >
                        {displayVreme.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '90%',
                      padding: '16px',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '12px',
                      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <FaMapMarkerAlt size={34} color="#3eb488" style={{ marginRight: '16px' }} />
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '20px',
                          fontWeight: '500',
                          marginBottom: '8px',
                          color: '#333',
                        }}
                      >
                        Ново место за седење
                      </label>
                      <select
                        value={newSeatingLocation}
                        onChange={(e) => setNewSeatingLocation(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '2px solid #3eb488',
                          borderRadius: '8px',
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#333',
                          backgroundColor: '#ffffff',
                          outline: 'none',
                          transition: 'border-color 0.2s ease-in-out',
                        }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#2a9066')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = '#3eb488')}
                      >
                        {displayLocacii.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <button
                  style={{
                    marginTop: '24px',
                    width: '80%',
                    maxWidth: '320px',
                    backgroundColor: '#3eb488',
                    color: '#ffffff',
                    padding: '16px',
                    borderRadius: '10px',
                    textAlign: 'center',
                    fontSize: '20px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    alignSelf: 'center',
                    display: 'block',
                    transition: 'background-color 0.2s ease-in-out, transform 0.2s ease-in-out',
                  }}
                  onClick={handleSuggestTime}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a9066';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3eb488';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  ПРЕДЛОЖИ
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default ReservationCard;